<vs-dialog [config]="{ dialogContainerClass: 'min-w-[535px] max-w-[800px]' }" (closed)="reset()">
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      showDivider: true,

      icon: 'filter-funnel-01',
      iconTheme: 'primary',

      title: usageLimitConfig.dialog.title,
      subtitle: headerSubtitle
    }"
  ></vs-dialog-header>

  <div class="p-4">
    <p class="text-sm font-medium text-gray-900 mt-2 mb-2">Configure Term Qualifying Conditions</p>
    <p class="text-sm font-normal text-gray-600 mb-2">
      Add conditions to this term so this plan is only shown for customers whose submitted information matches.
    </p>
    <p class="text-sm font-normal text-gray-600 mb-2">
      If no conditions are set, this term remains available for this plan.
    </p>

    <div *ngIf="!addCondition" class="w-full my-4 p-4 flex gap-4 justify-between">
      <div>
        <p class="text-sm font-semibold text-primary-700">No conditions created for this term</p>
        <p class="text-xs font-regular text-gray-600 mt-1">Create a new condition</p>
      </div>

      <button vs-button class="self-center" size="md" theme="primary" (click)="addCondition = true">
        <vs-icon name="plus"></vs-icon>
        Condition
      </button>
    </div>

    <form *ngIf="addCondition" [formGroup]="form">
      <ws-condition-group-builder
        *ngIf="propertySet$ | async as propertySet"
        class="pl-4 pr-4"
        formControlName="termConditions"
        [hideClose]="true"
        [propertySet]="propertySet"
        [includeDimensions]="true"
      >
      </ws-condition-group-builder>
    </form>

    <!-- Usage limits are part of the new claims flow -->
    <ng-container *ngIf="newClaimsFlow">
      <vs-divider class="my-4"></vs-divider>

      <p class="text-sm font-medium text-gray-900 mb-2">{{ usageLimitConfig.title }}</p>
      <p class="text-sm font-normal text-gray-600 mb-2">{{ usageLimitConfig.description }}</p>
      <p *ngIf="usageLimitsOn" class="text-sm font-normal text-gray-600 mb-2">{{ usageLimitConfig.hint }}</p>

      <ng-container *ngIf="usageLimitsOn; else usageLimitOff">
        <div *ngIf="!addUsageLimit" data-testid="usage-limit-empty" class="w-full my-4 p-4 flex gap-4 justify-between">
          <div>
            <p class="text-sm font-semibold text-primary-700">{{ usageLimitConfig.empty }}</p>
            <p class="text-xs font-regular text-gray-600 mt-1">{{ usageLimitConfig.emptyHint }}</p>
          </div>

          <button
            vs-button
            class="self-center"
            size="md"
            theme="primary"
            data-testid="usage-limit-add"
            (click)="addUsageLimit = true"
          >
            <vs-icon name="plus"></vs-icon>
            {{ usageLimitConfig.add }}
          </button>
        </div>

        <!-- Enter in a number field would "click" the first button in the form (add an if statement). -->
        <form
          *ngIf="addUsageLimit"
          class="flex flex-col gap-3 pl-4 pr-4"
          [formGroup]="form"
          data-testid="usage-limit-form"
          (keydown.enter)="$event.preventDefault()"
        >
          <ng-container *ngIf="usageDimensions$ | async as usageDimensions">
            <vs-form-field appearance="horizontal">
              <label>{{ usageLimitConfig.reading }}</label>
              <vs-select
                vsInput
                class="flex-1"
                formControlName="usageDimensionId"
                [config]="{ placeholder: usageLimitConfig.readingPlaceholder }"
              >
                <li *ngFor="let dimension of usageDimensions" vs-select-option [value]="dimension.id">
                  {{ dimension.title }}
                </li>
              </vs-select>
            </vs-form-field>

            <p *ngIf="!usageDimensions.length" class="text-sm text-gray-600">{{ usageLimitConfig.noReadings }}</p>

            <p class="text-sm font-medium text-gray-700">
              {{ form.value.usageDimensionId ? usageLimitConfig.limitIn(readingTitle(usageDimensions,
              form.value.usageDimensionId)) : usageLimitConfig.limit }}
            </p>
          </ng-container>

          <ws-condition-rule-builder
            *ngIf="propertySet$ | async as propertySet"
            type="number"
            formControlName="usageLimit"
            [currency]="false"
            [plan]="plan"
            [propertySet]="propertySet"
          ></ws-condition-rule-builder>

          <button
            vs-button
            class="self-start"
            size="sm"
            appearance="clear"
            theme="danger"
            data-testid="usage-limit-remove"
            (click)="removeUsageLimit()"
          >
            {{ usageLimitConfig.remove }}
          </button>
        </form>
      </ng-container>

      <ng-template #usageLimitOff>
        <div data-testid="usage-limit-off" class="w-full my-4 p-4">
          <p class="text-sm font-semibold text-gray-700">{{ usageLimitConfig.off.title }}</p>
          <p class="text-xs font-regular text-gray-600 mt-1">
            {{ savedLimitWhileOff ? usageLimitConfig.off.savedButOff : usageLimitConfig.off.hint }}
          </p>
        </div>
      </ng-template>

      <p *ngIf="usageLimitProblem as problem" data-testid="usage-limit-problem" class="text-sm text-gray-600 mt-3">
        {{ problem }}
      </p>
    </ng-container>

    <div class="flex justify-between items-center mt-4">
      <p *ngIf="policy?.status !== draft" class="flex gap-2 text-danger-500 text-sm">
        <vs-icon theme="danger" class="self-center" name="alert-circle" size="lg"></vs-icon>
        <span class="self-center">
          Saving will create a new policy version, the current policy will not be modified
        </span>
      </p>

      <div class="flex gap-2 ml-auto">
        <button vs-button size="md" appearance="outline" (click)="close()">Cancel</button>
        <button vs-button size="md" theme="primary" [disabled]="saving || usageLimitInvalid" (click)="save()">
          Save
        </button>
      </div>
    </div>
  </div>
</vs-dialog>
